import CompanyInteractions, { useInteractionSummary } from '@/components/features/Companies/components/CompanyInteractions';
import AddMeetingModal from '@/components/features/Companies/forms/AddMeetingModal';
import AddPhoneCallModal from '@/components/features/Companies/forms/AddPhoneCallModal';
import {
    leadFilterOptions as buildLeadFilterOptions,
    leadOptionSourcesFromRows,
    leadTableDescriptor,
} from '@/components/features/Leads/tables/lead-table-descriptor';
import { LeadCard } from '@/components/features/Leads/tables/LeadCard';
import { LeadTable } from '@/components/features/Leads/tables/LeadTable';
import EditPersonModal from '@/components/features/People/forms/EditPerson';
import {
    projectFilterOptions as buildProjectFilterOptions,
    projectOptionSourcesFromRows,
    projectTableDescriptor,
} from '@/components/features/Projects/tables/project-table-descriptor';
import { ProjectCard } from '@/components/features/Projects/tables/ProjectCard';
import { ProjectTable } from '@/components/features/Projects/tables/ProjectTable';
import { ConnectedActions, DeleteActionButton, DeleteConfirmAlert, DETAIL_ACTION_BUTTON_CLASS } from '@/components/global/detail-page-actions';
import { ResponsiveActions, type ResponsiveActionItem } from '@/components/global/ResponsiveActions';
import { EmbeddedResourceTable } from '@/components/ui/embedded-resource-table/EmbeddedResourceTable';
import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/shadcn/card';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/shadcn/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/shadcn/tabs';
import { useSettings } from '@/contexts/SettingsContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { useNavigationVisibilityUi, type NavVisibilityKey } from '@/hooks/useNavigationVisibilityUi';
import { useTranslations } from '@/hooks/useTranslations';
import ShowLayout from '@/layouts/show-layout';
import { ProjectStatusOption } from '@/lib/constants-db';
import { getCountryName } from '@/lib/country-utils';
import { settingLabel } from '@/lib/status-labels';
import { cn, formatCurrency, formatDateTime, useCan } from '@/lib/utils';
import { Company, Lead, PriorityOption, Project, StatusOption, type BreadcrumbItem, type People, type User as UserType } from '@/types';
import { Head, Link, router, usePage } from '@inertiajs/react';
import { format } from 'date-fns';
import {
    Activity,
    CalendarClock,
    CalendarDays,
    CircleDollarSign,
    FolderOpen,
    Mail,
    MapPin,
    Phone,
    SquarePen,
    Tag,
    Trash2,
    User,
    UserCog,
    UserRoundCheck,
} from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { route } from 'ziggy-js';

interface ShowPersonPageProps {
    person: People;
    leads?: Lead[];
    projects?: Project[];
    phoneCalls?: {
        id: string;
        description?: string | null;
        call_date: string;
        call_time?: string | null;
        call_direction?: string | null;
        call_result?: string | null;
        duration_minutes?: number | null;
        created_at: string;
        created_by?: UserType;
        createdBy?: UserType;
        participants?: People[];
    }[];
    meetings?: {
        id: string;
        description?: string | null;
        meeting_date: string;
        meeting_time?: string | null;
        meeting_format?: string | null;
        location?: string | null;
        created_at: string;
        created_by?: UserType;
        createdBy?: UserType;
        participants?: People[];
    }[];
    companies?: Company[];
    companyPeople?: People[];
    [key: string]: unknown;
}

/** A labelled value in the detail cards. Falls back to a muted placeholder when empty. */
function DetailField({ label, icon, fallback, children }: { label: string; icon?: ReactNode; fallback?: string; children?: ReactNode }) {
    return (
        <div className="space-y-1">
            <div className="text-muted-foreground flex items-center gap-1.5 text-xs font-medium">
                {icon}
                {label}
            </div>
            <div className="text-sm font-medium break-words">{children ?? <span className="text-muted-foreground font-normal">{fallback}</span>}</div>
        </div>
    );
}

/** One segment of the engagement strip: coloured icon, label, value. */
function MetricTile({ icon, tone, label, children }: { icon: ReactNode; tone: string; label: string; children: ReactNode }) {
    return (
        <div className="flex items-center gap-3 p-4">
            <span className={cn('inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg', tone)}>{icon}</span>
            <div className="min-w-0">
                <div className="text-muted-foreground text-xs font-medium">{label}</div>
                <div className="mt-0.5 text-lg font-semibold">{children}</div>
            </div>
        </div>
    );
}

/** A compact tile inside the sidebar statistics card — subtle, never a card of its own. */
function StatTile({ icon, label, children, className }: { icon: ReactNode; label: string; children: ReactNode; className?: string }) {
    return (
        <div className={cn('bg-muted/40 rounded-lg border p-3', className)}>
            {/* The sidebar column is narrow, so long labels wrap rather than truncate. */}
            <div className="text-muted-foreground flex items-start gap-1.5 text-xs font-medium">
                <span className="mt-0.5 shrink-0">{icon}</span>
                <span className="min-w-0">{label}</span>
            </div>
            <div className="mt-1 text-sm font-semibold break-words">{children}</div>
        </div>
    );
}

/** One label/value row closing out the sidebar cards. */
function SummaryRow({ icon, label, children }: { icon: ReactNode; label: string; children: ReactNode }) {
    return (
        <div className="flex items-start justify-between gap-3">
            <div className="text-muted-foreground flex items-center gap-1.5 text-xs font-medium">
                <span className="shrink-0">{icon}</span>
                {label}
            </div>
            <div className="min-w-0 text-right text-sm font-medium break-words">{children}</div>
        </div>
    );
}

/** Two-letter identity anchor for the assigned company tile. */
function companyInitials(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);

    if (parts.length === 0) {
        return '—';
    }

    return (parts.length === 1 ? parts[0].slice(0, 2) : `${parts[0][0]}${parts[1][0]}`).toUpperCase();
}

export default function ShowPerson() {
    const { props } = usePage<ShowPersonPageProps>();
    const { person, leads, projects, phoneCalls, meetings, companies, companyPeople = [] } = props;
    const { t } = useTranslations();
    const { settings } = useSettings();
    const isMobile = useIsMobile();
    const { can } = useCan();
    const { shouldShowForNavKey, isAdminOnlyHiddenNavKey, tabDisplayLabel } = useNavigationVisibilityUi();
    const breadcrumbs: BreadcrumbItem[] = [
        {
            title: t('people.index.breadcrumbs.contacts', 'Contacts'),
            href: '/people',
        },
        {
            title: `${person.first_name} ${person.last_name}`,
            href: `/person/${person.id}/show`,
        },
    ];

    // const personCompany = person.company ? [person.company] : [];
    const personLeads = useMemo(() => leads || [], [leads]);
    const personProjects = useMemo(() => projects || [], [projects]);
    const companyId = person.company?.id;

    const companyLeads = useMemo(() => (companyId ? personLeads.filter((lead) => lead.company_id === companyId) : []), [companyId, personLeads]);
    const companyProjects = useMemo(
        () => (companyId ? personProjects.filter((project) => project.company_id === companyId) : []),
        [companyId, personProjects],
    );

    const lastContactedAt = useMemo(() => {
        const leadDates = companyLeads
            .map((lead) => lead.last_contacted)
            .filter((value): value is string => Boolean(value))
            .map((value) => new Date(value));

        if (leadDates.length === 0) {
            return null;
        }

        return leadDates.reduce((latest, current) => (current > latest ? current : latest));
    }, [companyLeads]);

    const totalRevenueFromCompany = useMemo(() => {
        const projectRevenue = companyProjects.reduce((total, project) => total + Number(project.sales_price || 0), 0);

        return projectRevenue;
    }, [companyProjects]);

    const companyPhoneCallsCount = useMemo(() => {
        if (!companyId || !phoneCalls) {
            return 0;
        }

        return phoneCalls.filter((call) => (call.participants ?? []).some((participant) => participant.company_id === companyId)).length;
    }, [companyId, phoneCalls]);

    const companyMeetingsCount = useMemo(() => {
        if (!companyId || !meetings) {
            return 0;
        }

        return meetings.filter((meeting) => (meeting.participants ?? []).some((participant) => participant.company_id === companyId)).length;
    }, [companyId, meetings]);

    const companyLastActivityAt = useMemo(() => {
        if (!companyId) {
            return null;
        }

        const callDates = (phoneCalls ?? [])
            .filter((call) => (call.participants ?? []).some((participant) => participant.company_id === companyId))
            .map((call) => new Date(call.call_date));
        const meetingDates = (meetings ?? [])
            .filter((meeting) => (meeting.participants ?? []).some((participant) => participant.company_id === companyId))
            .map((meeting) => new Date(meeting.meeting_date));
        const allDates = [...callDates, ...meetingDates];
        if (allDates.length === 0) {
            return null;
        }

        return allDates.reduce((latest, current) => (current > latest ? current : latest));
    }, [companyId, meetings, phoneCalls]);

    // Same figures the activity list shows in its own summary cards — computed once,
    // rendered here as the engagement strip instead.
    const engagement = useInteractionSummary(phoneCalls || [], meetings || []);

    const company = person.company;

    const companyMainActivity = useMemo(() => {
        const raw = company?.category;
        const values = Array.isArray(raw) ? raw : raw ? [String(raw)] : [];

        return values.length > 0 ? values.map((value) => settingLabel(t, 'company_categories', value)).join(', ') : null;
    }, [company?.category, t]);

    const companyCountryLabel = company?.country ? getCountryName(company.country, settings.countries) || company.country : null;

    // The contact has no address of its own; this is the company's location.
    const companyAddressLines = useMemo(() => {
        if (!company) {
            return [];
        }

        const street = [company.address_street, company.address_house_number].filter(Boolean).join(' ').trim();
        const cityLine = [company.address_city, company.address_postal_code, companyCountryLabel].filter(Boolean).join(', ').trim();

        return [street, cityLine].filter((line) => line.length > 0);
    }, [company, companyCountryLabel]);

    /**
     * Filter options for both tabs. The columns, filters and sort entries come from
     * the resource descriptors, so these tables match their index pages; only the
     * option *sources* are local, derived from the rows this contact carries.
     */
    const leadsFilterOptions = useMemo(
        () =>
            buildLeadFilterOptions({
                t,
                statuses: (settings.leadStatuses as unknown as StatusOption[]) || [],
                priorities: (settings.leadPriorities as unknown as PriorityOption[]) || [],
                ...leadOptionSourcesFromRows(personLeads),
            }),
        [t, settings.leadStatuses, settings.leadPriorities, personLeads],
    );

    const projectsFilterOptions = useMemo(
        () =>
            buildProjectFilterOptions({
                t,
                statuses: (settings.projectStatuses as unknown as ProjectStatusOption[]) || [],
                ...projectOptionSourcesFromRows(personProjects),
            }),
        [t, settings.projectStatuses, personProjects],
    );

    const handleLeadsUpdate = {
        status: async (leadId: string, value: string) => {
            router.patch(`/leads/${leadId}/status`, { status: value }, { preserveState: true, preserveScroll: true });
        },
        priority: async (leadId: string, value: string) => {
            router.patch(`/leads/${leadId}/priority`, { priority: value }, { preserveState: true, preserveScroll: true });
        },
    };

    const handleProjectsUpdate = {
        status: async (projectId: string, value: string) => {
            router.patch(`/projects/${projectId}/status`, { status: value }, { preserveState: true, preserveScroll: true });
        },
    };

    const tabs = useMemo(() => {
        type Row = {
            value: string;
            navKey?: NavVisibilityKey;
            label: string;
            count?: number;
            content: ReactNode;
        };

        const rows: Row[] = [
            {
                value: 'details',
                label: t('people.show.tabs.details', 'Details'),
                content: (
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-4 lg:items-start">
                        {/* Main column — who they are and what has happened, at ~75%. */}
                        <div className="space-y-4 lg:col-span-3">
                            {/* Engagement headline, straight from the activity data. */}
                            <div className="bg-card grid grid-cols-1 divide-y overflow-hidden rounded-xl border sm:grid-cols-2 sm:divide-x xl:grid-cols-4 xl:divide-y-0">
                                <MetricTile
                                    icon={<Activity className="h-5 w-5" />}
                                    tone="bg-primary/12 text-primary"
                                    label={t('companies.interactions.summary.last_activity', 'Paskutinė veikla')}
                                >
                                    {engagement.lastActivity}
                                </MetricTile>
                                <MetricTile
                                    icon={<Phone className="h-5 w-5" />}
                                    tone="bg-success-subtle text-success-subtle-foreground"
                                    label={t('companies.interactions.summary.last_call', 'Paskutinis skambutis')}
                                >
                                    {engagement.lastCall}
                                </MetricTile>
                                <MetricTile
                                    icon={<CalendarDays className="h-5 w-5" />}
                                    tone="bg-info-subtle text-info-subtle-foreground"
                                    label={t('companies.interactions.summary.last_meeting', 'Paskutinis susitikimas')}
                                >
                                    {engagement.lastMeeting}
                                </MetricTile>
                                <MetricTile
                                    icon={<UserRoundCheck className="h-5 w-5" />}
                                    tone="bg-cat-purple text-cat-purple-foreground"
                                    label={t('companies.interactions.summary.status_label', 'Būsena')}
                                >
                                    <span className="flex flex-col items-start gap-1">
                                        <Badge className={engagement.status.className}>{engagement.status.label}</Badge>
                                        <span className="text-muted-foreground text-xs font-normal">{engagement.status.hint}</span>
                                    </span>
                                </MetricTile>
                            </div>

                            <Card>
                                <CardHeader className="pb-3">
                                    <CardTitle>{t('people.show.sections.contact_information', 'Contact information')}</CardTitle>
                                </CardHeader>
                                <CardContent className="grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2 xl:grid-cols-3">
                                    <DetailField icon={<User className="h-4 w-4" />} label={t('people.show.fields.full_name', 'Full Name')}>
                                        {`${person.first_name} ${person.last_name}`.trim()}
                                    </DetailField>
                                    <DetailField
                                        icon={<UserCog className="h-4 w-4" />}
                                        label={t('people.show.fields.position', 'Position')}
                                        fallback={t('people.show.empty.no_position', 'No position set')}
                                    >
                                        {person.position || null}
                                    </DetailField>
                                    <DetailField
                                        icon={<UserRoundCheck className="h-4 w-4" />}
                                        label={t('people.show.fields.accounting_contact', 'Accounting Contact')}
                                    >
                                        {person.is_accounting_contact ? t('common.yes', 'Yes') : t('common.no', 'No')}
                                    </DetailField>
                                    <DetailField
                                        icon={<Mail className="h-4 w-4" />}
                                        label={t('people.show.fields.email', 'Email')}
                                        fallback={t('people.show.empty.no_email', 'No email provided')}
                                    >
                                        {person.email ? (
                                            <a href={`mailto:${person.email}`} className="text-primary hover:underline">
                                                {person.email}
                                            </a>
                                        ) : null}
                                    </DetailField>
                                    <DetailField
                                        icon={<Phone className="h-4 w-4" />}
                                        label={t('people.show.fields.phone', 'Phone')}
                                        fallback={t('people.show.empty.no_phone', 'No phone provided')}
                                    >
                                        {person.phone ? (
                                            <a href={`tel:${person.phone}`} className="text-primary hover:underline">
                                                {person.phone}
                                            </a>
                                        ) : null}
                                    </DetailField>
                                </CardContent>
                            </Card>

                            {/* The former Activity tab — same component, same data, now part of
                                the overview. Its summary cards are hidden because the engagement
                                strip above already shows those figures. */}
                            <div className="space-y-3">
                                <h2 className="text-base font-semibold">{t('people.show.sections.recent_activity', 'Recent activity')}</h2>
                                <CompanyInteractions
                                    phoneCalls={phoneCalls || []}
                                    meetings={meetings || []}
                                    company={person.company as Company}
                                    people={(companyPeople.length > 0 ? companyPeople : [person]) as People[]}
                                    showSummary={false}
                                />
                            </div>
                        </div>

                        {/* Context column — the company relationship and supporting figures. */}
                        <div className="space-y-4 lg:col-span-1">
                            <Card>
                                <CardHeader className="flex flex-row items-center justify-between gap-3 pb-3">
                                    <CardTitle>{t('people.show.sections.assigned_company', 'Assigned company')}</CardTitle>
                                    {person.company && (
                                        <Link href={`/company/${person.company.id}`} className="text-primary shrink-0 text-sm hover:underline">
                                            {t('people.show.actions.view_company', 'View Company')}
                                        </Link>
                                    )}
                                </CardHeader>
                                <CardContent className="space-y-3">
                                    {person.company ? (
                                        <>
                                            <div className="flex items-start gap-2.5">
                                                <span className="bg-primary/12 text-primary inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-sm font-semibold">
                                                    {companyInitials(person.company.name)}
                                                </span>
                                                <div className="min-w-0 space-y-1">
                                                    <Link
                                                        href={`/company/${person.company.id}`}
                                                        className="hover:text-primary block text-sm font-semibold break-words"
                                                    >
                                                        {person.company.name}
                                                    </Link>
                                                    <div className="flex flex-wrap items-center gap-2">
                                                        {person.company.company_code && (
                                                            <Badge variant="secondary">{person.company.company_code}</Badge>
                                                        )}
                                                        {companyCountryLabel && (
                                                            <span className="text-muted-foreground text-xs">{companyCountryLabel}</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            <p className="text-muted-foreground text-xs">
                                                {t('people.show.company.relationship_hint', 'This contact is assigned to this company.')}
                                            </p>

                                            <div className="grid grid-cols-1 gap-x-4 gap-y-4 border-t pt-3 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
                                                <DetailField
                                                    label={t('people.show.company.main_activity', 'Main activity')}
                                                    fallback={t('common.not_specified', 'Not specified')}
                                                >
                                                    {companyMainActivity}
                                                </DetailField>
                                                <DetailField
                                                    label={t('people.show.company.client_type', 'Client type')}
                                                    fallback={t('common.not_specified', 'Not specified')}
                                                >
                                                    {person.company.type ? settingLabel(t, 'company_types', person.company.type) : null}
                                                </DetailField>
                                                <DetailField
                                                    label={t('people.show.company.created', 'Created')}
                                                    fallback={t('common.not_specified', 'Not specified')}
                                                >
                                                    {person.company.created_at ? formatDateTime(person.company.created_at) : null}
                                                </DetailField>
                                                {/* The company's location — the contact has no address of its own. */}
                                                <DetailField
                                                    icon={<MapPin className="h-4 w-4" />}
                                                    label={t('people.show.sections.location', 'Location')}
                                                    fallback={t('people.show.company.no_location', 'No address on the company record.')}
                                                >
                                                    {companyAddressLines.length > 0 ? (
                                                        <span className="space-y-0.5">
                                                            {companyAddressLines.map((line) => (
                                                                <span key={line} className="block break-words">
                                                                    {line}
                                                                </span>
                                                            ))}
                                                        </span>
                                                    ) : null}
                                                </DetailField>
                                            </div>
                                        </>
                                    ) : (
                                        <p className="text-muted-foreground text-sm">
                                            {t('people.show.empty.no_company', 'No company assigned to this person.')}
                                        </p>
                                    )}
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader className="pb-3">
                                    <CardTitle>{t('people.show.sections.statistics', 'Statistics')}</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-3">
                                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                        <StatTile
                                            icon={<CalendarClock className="h-3.5 w-3.5" />}
                                            label={t('people.show.company.last_contacted', 'Last Contacted')}
                                        >
                                            {lastContactedAt ? (
                                                formatDateTime(lastContactedAt)
                                            ) : (
                                                <span className="text-muted-foreground font-normal">
                                                    {t('people.show.empty.no_contact_yet', 'No contact history')}
                                                </span>
                                            )}
                                        </StatTile>
                                        <StatTile
                                            icon={<CircleDollarSign className="h-3.5 w-3.5" />}
                                            label={t('people.show.company.total_revenue', 'Total Revenue')}
                                        >
                                            {formatCurrency(totalRevenueFromCompany)}
                                        </StatTile>
                                        <StatTile icon={<Tag className="h-3.5 w-3.5" />} label={t('people.show.tabs.leads', 'Leads')}>
                                            {companyLeads.length}
                                        </StatTile>
                                        <StatTile icon={<FolderOpen className="h-3.5 w-3.5" />} label={t('people.show.tabs.projects', 'Projects')}>
                                            {companyProjects.length}
                                        </StatTile>
                                        <StatTile
                                            icon={<Phone className="h-3.5 w-3.5" />}
                                            label={t('people.show.company.phone_calls', 'Phone Calls')}
                                        >
                                            {companyPhoneCallsCount}
                                        </StatTile>
                                        <StatTile
                                            icon={<CalendarDays className="h-3.5 w-3.5" />}
                                            label={t('people.show.company.meetings', 'Meetings')}
                                        >
                                            {companyMeetingsCount}
                                        </StatTile>
                                        <StatTile
                                            icon={<Activity className="h-3.5 w-3.5" />}
                                            label={t('people.show.company.latest_activity', 'Latest Activity')}
                                        >
                                            {companyLastActivityAt ? (
                                                formatDateTime(companyLastActivityAt)
                                            ) : (
                                                <span className="text-muted-foreground font-normal">
                                                    {t('people.show.empty.no_activity_yet', 'No activity yet')}
                                                </span>
                                            )}
                                        </StatTile>
                                    </div>
                                    {/* Record metadata closes out the card — supporting, not headline. */}
                                    <div className="space-y-2.5 border-t pt-3">
                                        <SummaryRow
                                            icon={<CalendarClock className="h-3.5 w-3.5" />}
                                            label={t('people.show.sidebar.created', 'Created')}
                                        >
                                            {person.created_at ? (
                                                format(new Date(person.created_at), 'yyyy.MM.dd HH:mm')
                                            ) : (
                                                <span className="text-muted-foreground font-normal">
                                                    {t('common.not_specified', 'Not specified')}
                                                </span>
                                            )}
                                        </SummaryRow>
                                        <SummaryRow
                                            icon={<CalendarClock className="h-3.5 w-3.5" />}
                                            label={t('people.show.sidebar.modified', 'Modified')}
                                        >
                                            {person.updated_at ? (
                                                format(new Date(person.updated_at), 'yyyy.MM.dd HH:mm')
                                            ) : (
                                                <span className="text-muted-foreground font-normal">
                                                    {t('common.not_specified', 'Not specified')}
                                                </span>
                                            )}
                                        </SummaryRow>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                ),
            },
            {
                value: 'leads',
                navKey: 'leads',
                label: t('people.show.tabs.leads', 'Leads'),
                count: personLeads.length,
                content: (
                    <EmbeddedResourceTable
                        descriptor={leadTableDescriptor}
                        rows={personLeads}
                        storageKey="leads:person-show"
                        filterOptions={leadsFilterOptions}
                        emptyMessage={t('leads.index.empty.no_leads_found', 'No leads found.')}
                        renderCard={(lead) => <LeadCard lead={lead} />}
                        renderTable={({ rows, tableProps }) => (
                            <LeadTable
                                {...tableProps}
                                leads={rows}
                                onUpdate={handleLeadsUpdate}
                                statuses={(settings.leadStatuses as unknown as StatusOption[]) || []}
                                priorities={(settings.leadPriorities as unknown as PriorityOption[]) || []}
                                approvalThreshold={settings.leadOfferApprovalThreshold}
                            />
                        )}
                    />
                ),
            },
            {
                value: 'projects',
                navKey: 'projects',
                label: t('people.show.tabs.projects', 'Projects'),
                count: personProjects.length,
                content: (
                    <EmbeddedResourceTable
                        descriptor={projectTableDescriptor}
                        rows={personProjects}
                        storageKey="projects:person-show"
                        filterOptions={projectsFilterOptions}
                        emptyMessage={t('projects.index.empty.no_projects_found', 'No projects found.')}
                        renderCard={(project) => <ProjectCard project={project} />}
                        renderTable={({ rows, tableProps }) => (
                            <ProjectTable
                                {...tableProps}
                                projects={rows}
                                onUpdate={handleProjectsUpdate}
                                statuses={(settings.projectStatuses as unknown as ProjectStatusOption[]) || []}
                            />
                        )}
                    />
                ),
            },
        ];

        return rows
            .filter((row) => shouldShowForNavKey(row.navKey))
            .map((row) => ({
                value: row.value,
                label: row.label,
                count: row.count,
                displayLabel: tabDisplayLabel(row.label, row.navKey, row.count),
                hiddenFromMainNav: isAdminOnlyHiddenNavKey(row.navKey),
                content: row.content,
            }));
    }, [
        person,
        personLeads,
        personProjects,
        companyLeads.length,
        companyProjects.length,
        lastContactedAt,
        totalRevenueFromCompany,
        t,
        shouldShowForNavKey,
        isAdminOnlyHiddenNavKey,
        tabDisplayLabel,
        settings.projectStatuses,
        settings.leadStatuses,
        settings.leadPriorities,
        settings.leadOfferApprovalThreshold,
        leadsFilterOptions,
        projectsFilterOptions,
        meetings,
        phoneCalls,
    ]);

    const defaultTab = useMemo(() => {
        const preferredOrder = ['details', 'leads', 'projects'];
        for (const value of preferredOrder) {
            if (tabs.some((tab) => tab.value === value)) {
                return value;
            }
        }

        return tabs[0]?.value ?? 'details';
    }, [tabs]);

    const [activeTab, setActiveTab] = useState(defaultTab);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const canDeletePerson = can('contacts.delete');
    const desktopMaxVisibleTabs = 4;
    const mainNavTabs = tabs.filter((tab) => !tab.hiddenFromMainNav);
    const hiddenNavTabs = tabs.filter((tab) => tab.hiddenFromMainNav);
    const visibleTabs = isMobile ? mainNavTabs : mainNavTabs.slice(0, desktopMaxVisibleTabs);
    const overflowTabs = isMobile ? hiddenNavTabs : [...mainNavTabs.slice(desktopMaxVisibleTabs), ...hiddenNavTabs];
    const hasOverflow = overflowTabs.length > 0;
    const tabIcons: Record<string, ReactNode> = {
        details: <User className="h-4 w-4" />,
        leads: <Tag className="h-4 w-4" />,
        projects: <FolderOpen className="h-4 w-4" />,
    };

    const actionItems: ResponsiveActionItem[] = [
        // Meeting and call are one segmented control: they are logged together and
        // must never be split across the row or into the overflow menu separately.
        ...(person.company
            ? [
                  {
                      key: 'log-interaction',
                      label: t('companies.forms.add_meeting.buttons.add_meeting', 'Add Meeting'),
                      icon: <CalendarDays className="h-4 w-4" />,
                      node: (
                          <ConnectedActions>
                              <AddMeetingModal
                                  company={person.company as Company}
                                  people={companyPeople as People[]}
                                  defaultParticipantIds={[String(person.id)]}
                                  trigger={
                                      <Button type="button" variant="outline" size="sm" className={DETAIL_ACTION_BUTTON_CLASS}>
                                          <CalendarDays className="h-4 w-4" />
                                          {t('companies.forms.add_meeting.buttons.add_meeting', 'Add Meeting')}
                                      </Button>
                                  }
                              />
                              <AddPhoneCallModal
                                  company={person.company as Company}
                                  users={companyPeople as People[]}
                                  defaultParticipantIds={[String(person.id)]}
                                  trigger={
                                      <Button type="button" variant="outline" size="sm" className={DETAIL_ACTION_BUTTON_CLASS}>
                                          <Phone className="h-4 w-4" />
                                          {t('companies.forms.add_phone_call.buttons.add_phone_call', 'Add Phone Call')}
                                      </Button>
                                  }
                              />
                          </ConnectedActions>
                      ),
                  } satisfies ResponsiveActionItem,
              ]
            : []),
        {
            key: 'edit',
            label: t('common.edit', 'Edit'),
            icon: <SquarePen className="h-4 w-4" />,
            pinned: true,
            node: (
                <EditPersonModal
                    person={person}
                    companies={companies || []}
                    trigger={
                        <Button type="button" variant="outline" size="sm" className={DETAIL_ACTION_BUTTON_CLASS}>
                            <SquarePen className="h-4 w-4" />
                            {t('common.edit', 'Edit')}
                        </Button>
                    }
                />
            ),
        },
        ...(canDeletePerson
            ? [
                  {
                      key: 'delete',
                      label: t('people.show.actions.delete', 'Delete Contact'),
                      icon: <Trash2 className="h-4 w-4" />,
                      destructive: true,
                      pinned: true,
                      onOverflowSelect: () => setDeleteConfirmOpen(true),
                      node: (
                          <DeleteActionButton label={t('people.show.actions.delete', 'Delete Contact')} onClick={() => setDeleteConfirmOpen(true)} />
                      ),
                  } satisfies ResponsiveActionItem,
              ]
            : []),
    ];

    return (
        <>
            <Head title={t('people.show.title', 'Contact Details')} />
            <ShowLayout breadcrumbs={breadcrumbs} title={`${person.first_name} ${person.last_name}`}>
                <Tabs value={activeTab} onValueChange={setActiveTab} className="flex min-h-0 w-full flex-1 flex-col">
                    <div className="mb-6 flex min-w-0 flex-col gap-3 lg:flex-row lg:items-center lg:gap-4">
                        <div className="min-w-0 lg:flex-[3]">
                            {isMobile ? (
                                <div className="-mx-1 overflow-x-auto pb-1">
                                    <div className="flex w-max min-w-full items-stretch gap-2 px-1">
                                        {[...visibleTabs, ...overflowTabs].map((tab) => {
                                            const isActive = activeTab === tab.value;
                                            return (
                                                <button
                                                    key={tab.value}
                                                    type="button"
                                                    onClick={() => setActiveTab(tab.value)}
                                                    className={cn(
                                                        'h-auto min-w-[120px] rounded-xl border px-4 py-3 text-left transition-colors',
                                                        isActive
                                                            ? 'border-primary/40 bg-primary/10 text-primary'
                                                            : 'border-border bg-background text-foreground',
                                                    )}
                                                >
                                                    <span className="flex flex-col items-start gap-1.5">
                                                        <span className="text-muted-foreground">{tabIcons[tab.value]}</span>
                                                        <span className="text-sm leading-tight font-medium">{tab.displayLabel}</span>
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            ) : (
                                <TabsList
                                    className={cn(
                                        'bg-muted flex h-auto min-w-0 flex-1 flex-nowrap items-center gap-1 rounded-xl px-1 py-1 sm:w-fit sm:flex-initial',
                                    )}
                                >
                                    {visibleTabs.map((tab) => (
                                        <TabsTrigger key={tab.value} value={tab.value} className={cn('rounded-md text-sm')}>
                                            <span className="inline-flex items-center gap-2">
                                                {tabIcons[tab.value]}
                                                {tab.displayLabel}
                                            </span>
                                        </TabsTrigger>
                                    ))}
                                    {hasOverflow && (
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <button
                                                    type="button"
                                                    className={cn(
                                                        'focus-visible:ring-ring inline-flex items-center justify-center rounded-md px-3 py-1.5 text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50',
                                                        overflowTabs.find((x) => x.value === activeTab)
                                                            ? 'bg-secondary text-secondary-foreground'
                                                            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                                                    )}
                                                >
                                                    {overflowTabs.find((x) => x.value === activeTab)
                                                        ? overflowTabs.find((x) => x.value === activeTab)?.displayLabel
                                                        : t('work_orders.show.tabs.more', '+{count} more').replace(
                                                              '{count}',
                                                              String(overflowTabs.length),
                                                          )}
                                                </button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end" className="min-w-[150px]">
                                                {overflowTabs.map((tab) => (
                                                    <DropdownMenuItem
                                                        key={tab.value}
                                                        onClick={() => setActiveTab(tab.value)}
                                                        className={cn(activeTab === tab.value && 'bg-accent font-medium')}
                                                    >
                                                        <span className="inline-flex items-center gap-2">
                                                            {tabIcons[tab.value]}
                                                            {tab.displayLabel}
                                                        </span>
                                                    </DropdownMenuItem>
                                                ))}
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    )}
                                </TabsList>
                            )}
                        </div>

                        <div className="hidden min-w-0 lg:flex lg:flex-[2]">
                            <ResponsiveActions className="w-full" items={actionItems} />
                        </div>
                    </div>

                    {tabs.map((tab) => (
                        <TabsContent key={tab.value} value={tab.value} className="mt-0 outline-none data-[state=inactive]:hidden">
                            {tab.content}
                        </TabsContent>
                    ))}
                </Tabs>
            </ShowLayout>

            <DeleteConfirmAlert
                open={deleteConfirmOpen}
                onOpenChange={setDeleteConfirmOpen}
                title={t('people.show.delete.confirm_title', 'Delete Contact')}
                description={t(
                    'people.show.delete.confirm_description',
                    'Are you sure you want to delete ":name"? This action cannot be undone.',
                ).replace(':name', `${person.first_name} ${person.last_name}`)}
                cancelLabel={t('people.show.delete.cancel', 'Cancel')}
                confirmLabel={t('people.show.delete.confirm_button', 'Delete')}
                onConfirm={() => {
                    setDeleteConfirmOpen(false);
                    router.delete(route('people.destroy', person.id), {
                        onSuccess: () => toast.success(t('people.show.delete.success', 'Contact deleted.')),
                        onError: () => toast.error(t('people.show.delete.failed', 'Failed to delete contact.')),
                    });
                }}
            />
        </>
    );
}
